import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  Box,
  Typography,
  Backdrop
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RowSousDossier, ActionButtonStateType } from './RowSousDossier';
import { styled } from '@mui/material/styles';
import { SubFolder } from '@/taskpane/types/folder';
import { NotificationModal } from '../notification-modal';

export interface SousDossierItem {
  id: string | number;
  text: string;
  actionButtonState: ActionButtonStateType;
  // Add any other properties needed for each item
}

interface PaletteSousDossierProps {
  open: boolean;
  onClose: () => void;
  items: SubFolder[];
  onItemActionClick?: (item: SubFolder) => void;
  title?: string;
  className?: string;
  selectedFolderId: string;
  onItemStatusUpdateRequest?: (dossierId: string, subFolder: SubFolder) => Promise<boolean>;
}

const StyledBackdrop = styled(Backdrop, {
  shouldForwardProp: (prop) => prop !== 'blurAmount',
})<{ blurAmount?: number }>(({ blurAmount = 2 }) => ({
  backdropFilter: `blur(${blurAmount}px)`,
}));

export const PaletteSousDossier: React.FC<PaletteSousDossierProps> = ({
  open,
  onClose,
  items,
  selectedFolderId,
  onItemActionClick,
  onItemStatusUpdateRequest,
  title = 'Sélectionnez un sous-dossier',
  className,
}) => {
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const blurAmount = 4;
    const handleInternalClick = async (item: SubFolder, actionButtonState: ActionButtonStateType) => {
      if (actionButtonState === 'activer') {
        if (onItemStatusUpdateRequest) {
          const success = await onItemStatusUpdateRequest(selectedFolderId, item);
          if (success) {
            setIsNotificationOpen(true);
          }
        }
      } else if (actionButtonState === 'preparation') {
        setIsNotificationOpen(true);
      } else {
        if (onItemActionClick) {
            onItemActionClick(item);
        }
      }
    }

    return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className={className}
      PaperProps={{ sx: { borderRadius: '16px', zIndex: (theme) => theme.zIndex.modal + 1 } }}
      BackdropComponent={StyledBackdrop}
      slotProps={{
        backdrop: {
          ...(blurAmount !== undefined && { blurAmount: blurAmount }),
          sx: { zIndex: (theme) => theme.zIndex.modal,
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(10px)'
           }
        }
      }}
    >
      <DialogTitle sx={{ padding: 0 }}>
      <Box
            sx={{
                position: 'relative', // Added for positioning context of the icon
                display: 'flex',
                justifyContent: 'center', // Changed to center the title
                alignItems: 'center',
                background: 'linear-gradient(to right, #4A00E0, #8E2DE2)',
                color: 'white',
                padding: (theme) => theme.spacing(1.5, 3), // Adjusted padding slightly for balance
                borderTopLeftRadius: '16px',
                borderTopRightRadius: '16px',
                minHeight: '56px', // Ensures consistent header height
            }}
        >
            <Typography
                variant="h6"
                component="div"
                sx={{
                    fontWeight: '700',
                    fontSize: '16px',
                    '@media (max-width: 370px)': {
                        fontSize: '12px',
                    },
                    fontStyle: 'normal',
                    lineHeight: 'normal',
                }}
            >
                {title}
            </Typography>
            <IconButton
                onClick={onClose}
                sx={{
                    color: 'white',
                    position: 'absolute', // Position icon absolutely
                    right: (theme) => theme.spacing(1), // Distance from the right
                    top: '50%', // Vertical centering
                    transform: 'translateY(-50%)', // Vertical centering
                }}
            >
                <CloseIcon />
            </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <List disablePadding>
          {items.map((item) => {
            let dateStringInput = item.sous_dossier_rag_indexed_requested_on;
            let processedDateString = dateStringInput;
            if (typeof dateStringInput === 'string') {
                // Replace space with 'T' if it's in "YYYY-MM-DD HH:MM:SS..." format because of formating mismatch between the API and the frontend
                if (dateStringInput.includes(' ') && !dateStringInput.includes('T')) {
                    processedDateString = dateStringInput.replace(' ', 'T');
                }
                if (processedDateString.includes('T') && !/Z|[+-]\d{2}(:\d{2})?$/.test(processedDateString)) {
                    processedDateString += 'Z';
                }
            }
            if (item.sous_dossier_rag_indexed_status === 'ready' && open && onItemActionClick) {
                onItemActionClick(item);
            }
            const requestedOnDate = new Date(processedDateString);
            const now = new Date();
            const thirtyMinutesInMs = 30 * 60 * 1000;
            const timeDifferenceMs = now.getTime() - requestedOnDate.getTime();
            const isRequestedInLast30Min = !isNaN(requestedOnDate.getTime()) && timeDifferenceMs < thirtyMinutesInMs;
            const actionButtonState = item.sous_dossier_rag_indexed_status === 'not_indexed' || item.sous_dossier_rag_indexed_status === ''
              ? 'activer'
              : item.sous_dossier_rag_indexed_status === 'requested' && isRequestedInLast30Min
              ? 'preparation'
              : 'discutez';

              return(
              <RowSousDossier
                key={item.index}
                selectedFolderId={selectedFolderId}
                selectedSubFolderId={item.sous_dossier_id}
                text={item.nature + " - " + item.name}
                actionButtonState={actionButtonState}
                onActionClick={onItemActionClick || item.sous_dossier_rag_indexed_status === 'not_indexed' ? () => handleInternalClick(item, actionButtonState) : undefined}
              />
              )
            })
          }

        </List>
        <NotificationModal
        open={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
        onButtonClick={() => setIsNotificationOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}; 